import { logger } from './logger';
const getDefaultMediaSession = () => typeof navigator !== 'undefined' && 'mediaSession' in navigator
    ? navigator.mediaSession
    : undefined;
const getDefaultCreateMetadata = () => typeof MediaMetadata === 'function'
    ? init => new MediaMetadata(init)
    : undefined;
/**
 * Whether the platform treats the capture state setters as a command rather
 * than as a report.
 *
 * Chromium's `setMicrophoneActive`/`setCameraActive` are purely declarative:
 * they only choose which glyph the media control draws. WebKit's are
 * imperative, and route through `UserMediaController::updateCaptureState` to
 * the page level capture muted state, which mutes the real tracks. Activating
 * capture there additionally requires a visible document and a user gesture,
 * and can raise a permission prompt.
 *
 * That asymmetry is a trap for a state driven caller: muting always succeeds,
 * while the matching unmute is rejected outside a gesture, leaving the
 * microphone muted at the page level with no way back from a signal handler.
 *
 * `setScreenshareActive` distinguishes the two, as only WebKit implements it.
 * WebKit exposes all three under a single `MediaSessionCaptureToggleAPIEnabled`
 * setting, so they appear and disappear together, which makes the probe
 * reliable: when the setting is off none of them exist and there is nothing to
 * call, and Chromium only ever exposes the microphone and the camera ones.
 */
const hasImperativeCaptureState = (mediaSession) => typeof mediaSession.setScreenshareActive === 'function';
const createUnsupportedMediaSession = () => ({
    isSupported: false,
    setMetadata: () => undefined,
    setPlaybackState: () => undefined,
    setMicrophoneActive: () => Promise.resolve(),
    setCameraActive: () => Promise.resolve(),
    setScreenshareActive: () => Promise.resolve(),
    setPositionState: () => undefined,
    registerActions: () => undefined,
    deregisterActions: () => undefined,
    destroy: () => undefined,
});
/**
 * Create a media session, presenting the ongoing call to the platform's media
 * controls, e.g. the lock screen, the notification shade or headset buttons.
 *
 * Declaring a media session also hints the browser that the page is playing
 * media, which mobile browsers use to relax the throttling applied to
 * backgrounded pages and to keep the audio capture alive.
 *
 * Every method is a no-op when the Media Session API is unavailable, so the
 * caller does not need to feature detect it.
 *
 * @example
 * ```ts
 * const session = createMediaSession({
 *     metadata: {title: 'Weekly sync', artist: 'Pexip'},
 *     handlers: {
 *         hangup: () => leaveMeeting(),
 *         togglemicrophone: () => toggleAudioMuted(),
 *     },
 * });
 * await session.setMicrophoneActive(true);
 * session.destroy();
 * ```
 */
export const createMediaSession = ({ handlers = {}, metadata, playbackState = 'playing', positionState, applyImperativeCaptureState = false, mediaSession = getDefaultMediaSession(), createMetadata = getDefaultCreateMetadata(), } = {}) => {
    if (!mediaSession) {
        logger.debug({ context: 'MediaSession' }, 'Media Session API is not supported');
        return createUnsupportedMediaSession();
    }
    const registeredActions = new Set();
    let destroyed = false;
    const skipCaptureState = !applyImperativeCaptureState && hasImperativeCaptureState(mediaSession);
    if (skipCaptureState) {
        logger.debug({ context: 'MediaSession' }, 'Skipping the media session capture state, the platform applies it to the real tracks');
    }
    const setActionHandler = (action, handler) => {
        try {
            mediaSession.setActionHandler(action, handler);
            return true;
        }
        catch (error) {
            logger.debug({ context: 'MediaSession', action, error }, 'Media session action is not supported');
            return false;
        }
    };
    const setMetadata = (updatedMetadata) => {
        if (destroyed) {
            return;
        }
        if (!updatedMetadata || !createMetadata) {
            mediaSession.metadata = null;
            return;
        }
        mediaSession.metadata = createMetadata(updatedMetadata);
    };
    const setPlaybackState = (updatedPlaybackState) => {
        if (destroyed) {
            return;
        }
        mediaSession.playbackState = updatedPlaybackState;
    };
    const setMicrophoneActive = async (active) => {
        if (destroyed ||
            skipCaptureState ||
            !mediaSession.setMicrophoneActive) {
            return;
        }
        try {
            await mediaSession.setMicrophoneActive(active);
        }
        catch (error) {
            logger.debug({ context: 'MediaSession', active, error }, 'Failed to update the media session microphone state');
        }
    };
    const setCameraActive = async (active) => {
        if (destroyed || skipCaptureState || !mediaSession.setCameraActive) {
            return;
        }
        try {
            await mediaSession.setCameraActive(active);
        }
        catch (error) {
            logger.debug({ context: 'MediaSession', active, error }, 'Failed to update the media session camera state');
        }
    };
    const setScreenshareActive = async (active) => {
        if (destroyed ||
            skipCaptureState ||
            !mediaSession.setScreenshareActive) {
            return;
        }
        try {
            await mediaSession.setScreenshareActive(active);
        }
        catch (error) {
            logger.debug({ context: 'MediaSession', active, error }, 'Failed to update the media session screenshare state');
        }
    };
    const setPositionState = (state) => {
        if (destroyed || !mediaSession.setPositionState) {
            return;
        }
        try {
            // Blink rejects a missing, `NaN` or negative duration, a negative
            // position, a position past the duration and a zero playback rate.
            mediaSession.setPositionState(state);
        }
        catch (error) {
            logger.debug({ context: 'MediaSession', state, error }, 'Failed to update the media session position state');
        }
    };
    const destroy = () => {
        if (destroyed) {
            return;
        }
        for (const action of registeredActions) {
            setActionHandler(action, null);
        }
        registeredActions.clear();
        setMetadata();
        setPlaybackState('none');
        setPositionState();
        // The session is a view onto the global `navigator.mediaSession`, which
        // outlives it, so every mutator has to stop writing to it once the
        // session is gone. Owners tear down on different clocks, e.g. the view
        // layer only unbinds its media element listeners on the next React
        // passive cleanup, well after the service has ended the call.
        destroyed = true;
    };
    const registerActions = handlers => {
        if (destroyed) {
            return;
        }
        for (const [action, handler] of Object.entries(handlers)) {
            if (!handler) {
                continue;
            }
            if (setActionHandler(action, handler)) {
                registeredActions.add(action);
            }
        }
    };
    const deregisterActions = actions => {
        if (destroyed) {
            return;
        }
        for (const action of actions) {
            if (registeredActions.delete(action)) {
                setActionHandler(action, null);
            }
        }
    };
    // Register initial actions
    registerActions(handlers);
    setMetadata(metadata);
    mediaSession.playbackState = playbackState;
    if (positionState) {
        setPositionState(positionState);
    }
    return {
        isSupported: true,
        setMetadata,
        setPlaybackState,
        setMicrophoneActive,
        setCameraActive,
        setScreenshareActive,
        setPositionState,
        registerActions,
        deregisterActions,
        destroy,
    };
};
//# sourceMappingURL=mediaSession.js.map